import { useState, useEffect } from "react";
import { listVersions, getVersion, recalculateAnalysis } from "../../services/api";

// ── Versões do cálculo (só admin) ─────────────────────────────────────────────
// A versão "original" é a própria análise; cada recálculo vira uma versão nova
// no servidor, sem apagar as anteriores. A escolha da versão exibida vale só
// para esta tela: ao abrir a análise de novo, volta a original.

function fmtDate(d) {
  return new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function versionLabel(v, i) {
  if (v.id === "original") return `Original · ${fmtDate(v.created_at)}`;
  const commit = v.commit ? v.commit.slice(0, 7) : "commit ?";
  const params = v.params_hash ? v.params_hash.slice(0, 8) : "?";
  return `Versão ${i} · ${fmtDate(v.created_at)} · ${commit} · parâm. ${params}`;
}

function fmt(v, digits = 2) {
  return v == null ? "—" : Number(v).toFixed(digits);
}

export default function Versions({ analysis, selectedId, onSelect }) {
  const [versions, setVersions] = useState([]);
  const [cache,    setCache]    = useState({});       // id -> versão completa
  const [busy,     setBusy]     = useState(false);
  const [error,    setError]    = useState("");
  const [notice,   setNotice]   = useState("");
  const [cmpA,     setCmpA]     = useState("original");
  const [cmpB,     setCmpB]     = useState("");

  const hasOriginal = Boolean(analysis.original_image);

  const reload = () =>
    listVersions(analysis.id)
      .then(setVersions)
      .catch(() => setError("Não foi possível carregar as versões."));

  useEffect(() => { reload(); }, [analysis.id]);

  // Versão completa (a original vem da própria análise já carregada)
  const full = async (id) => {
    if (id === "original") return analysis;
    if (cache[id]) return cache[id];
    const v = await getVersion(analysis.id, id);
    setCache((c) => ({ ...c, [id]: v }));
    return v;
  };

  const select = async (id) => {
    setError("");
    try {
      onSelect(id === "original" ? null : await full(Number(id)));
    } catch {
      setError("Não foi possível carregar a versão.");
    }
  };

  const recalc = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const v = await recalculateAnalysis(analysis.id);
      setCache((c) => ({ ...c, [v.id]: v }));
      await reload();
      setNotice("Nova versão calculada. A versão exibida não mudou; escolha-a na lista para ver.");
      if (!cmpB) setCmpB(String(v.id));
    } catch (e) {
      setError(e.response?.data?.detail || "Falha ao recalcular.");
    } finally {
      setBusy(false);
    }
  };

  // Comparação lado a lado: carrega as duas versões escolhidas sob demanda
  const [pair, setPair] = useState([null, null]);
  useEffect(() => {
    let alive = true;
    const ids = [cmpA, cmpB].map((x) => (x === "" ? null : x === "original" ? x : Number(x)));
    Promise.all(ids.map((x) => (x == null ? null : full(x))))
      .then((p) => alive && setPair(p))
      .catch(() => alive && setError("Não foi possível carregar a comparação."));
    return () => { alive = false; };
  }, [cmpA, cmpB, analysis]);

  const indexOf = (id) => versions.findIndex((v) => String(v.id) === String(id));
  const nameOf  = (id) => {
    const i = indexOf(id);
    return i < 0 ? "" : i === 0 ? "Original" : `Versão ${i}`;
  };

  return (
    <div className="chart-card versions-card">
      <h2>Versões do cálculo</h2>
      <p className="chart-subtitle">
        Exibindo: <strong>{nameOf(selectedId ?? "original") || "Original"}</strong>
        {selectedId == null && " (padrão)"}
      </p>

      <div className="versions-row">
        <label className="versions-field">
          <span>Versão exibida</span>
          <select value={selectedId ?? "original"} onChange={(e) => select(e.target.value)}>
            {versions.map((v, i) => (
              <option key={v.id} value={v.id}>{versionLabel(v, i)}</option>
            ))}
          </select>
        </label>
        <button
          className="btn-export"
          onClick={recalc}
          disabled={!hasOriginal || busy}
          title={hasOriginal ? "" : "Esta análise não tem a imagem original salva"}
        >
          {busy ? "Recalculando..." : "Recalcular com o algoritmo atual"}
        </button>
      </div>

      {!hasOriginal && (
        <p className="versions-note">
          Recalcular indisponível: esta análise não tem a imagem original salva
          (a opção KEEP_ORIGINAL_IMAGES estava desligada quando ela foi criada).
        </p>
      )}
      {notice && <p className="versions-note">{notice}</p>}
      {error && <p className="versions-error">{error}</p>}

      {versions.length > 1 && (
        <>
          <h3 className="versions-subtitle">Comparar duas versões</h3>
          <div className="versions-row">
            {[[cmpA, setCmpA], [cmpB, setCmpB]].map(([val, set], k) => (
              <label key={k} className="versions-field">
                <span>{k === 0 ? "A" : "B"}</span>
                <select value={val} onChange={(e) => set(e.target.value)}>
                  {k === 1 && <option value="">—</option>}
                  {versions.map((v, i) => (
                    <option key={v.id} value={v.id}>{versionLabel(v, i)}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          <div className="versions-compare">
            {pair.map((v, k) => (
              <div key={k} className="versions-col">
                {v ? (
                  <>
                    <div className="versions-col-title">{nameOf(k === 0 ? cmpA : cmpB)}</div>
                    <dl className="versions-stats">
                      <dt>Grãos</dt><dd>{v.metrics?.count ?? "—"}</dd>
                      <dt>Diâmetro médio</dt><dd>{fmt(v.metrics?.mean_mm)} mm</dd>
                      <dt>Área média</dt><dd>{fmt(v.metrics?.area_mean_mm2)} mm²</dd>
                    </dl>
                    {v.annotated_image && (
                      <img
                        src={`data:image/jpeg;base64,${v.annotated_image}`}
                        alt={`Overlay ${nameOf(k === 0 ? cmpA : cmpB)}`}
                        className="annotated-img"
                      />
                    )}
                  </>
                ) : (
                  <div className="versions-col-empty">Escolha uma versão</div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
